import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { useDateNow, useReduxState } from 'hooks';
import {
  getCourseLanguageVersionOptions,
  getOpeningHoursWeekDays
} from 'lib/courses';
import { getMessageWithPlaceholders } from 'shared-locales';
import { breakpoints } from 'styles';
import {
  Badge,
  ContentLabel,
  ContentValue,
  DisplayFormattedDate,
  FlagIcon,
  FlexContainer,
  Link,
  RichTextValue,
  User
} from 'components/common';
import { DisplayAttributeInProductPage } from 'components/common/customAttributes';
import { CompetenciesProductBadge } from 'components/competencies';
import { CourseLanguageSelection } from 'components/courses/common';

import type {
  CompetencyInProduct,
  Course,
  CourseCompletion,
  CourseWeekDay,
  UserWithBasicData
} from 'shared-types';

interface Props {
  course: Pick<
    Course,
    | 'customAttributes'
    | 'completionWorkload'
    | 'completionWorkloadValue'
    | 'description'
    | 'endDate'
    | 'id'
    | 'openingHours'
    | 'picture'
    | 'picturePosition'
    | 'startDate'
    | 'title'
    | 'language'
    | 'languageVersions'
  > & {
    categories: string[];
    competencies: CompetencyInProduct[];
    isMandatoryInView?: boolean;
    owners: UserWithBasicData[];
  };
  courseCompletionLanguage: CourseCompletion['courseLanguageVersion'];
  displayPreviewLanguageVersion: Course['language'];
  enableCourseCompletionLanguageSelection: boolean;
  isProfilePage?: boolean;
  onLanguageVersionChange?: () => Promise<void>;
  titleLink?: string;
}

const CourseInfo = ({
  course,
  courseCompletionLanguage,
  displayPreviewLanguageVersion,
  enableCourseCompletionLanguageSelection,
  isProfilePage = false,
  onLanguageVersionChange,
  titleLink
}: Props) => {
  const { dateNow: now } = useDateNow();

  const { language, messages, serverConfig, theme } = useReduxState();

  const router = useRouter();

  const handleDisplayLanguageChange = (language: string) => {
    router.replace({ query: { ...router.query, languageVersion: language } });
  };

  const labelSpacing = isProfilePage ? 30 : 20;

  const availableLanguages = getCourseLanguageVersionOptions({
    course,
    onlyPublished: true
  });

  const selectedLanguage = useMemo(() => {
    if (
      courseCompletionLanguage &&
      availableLanguages[courseCompletionLanguage]
    ) {
      return courseCompletionLanguage;
    }
    return null;
  }, [availableLanguages, courseCompletionLanguage]);

  const courseDescription =
    displayPreviewLanguageVersion === course.language
      ? course.description
      : course.languageVersions?.[displayPreviewLanguageVersion]
          ?.courseDescription || '';

  const courseTitle =
    course.languageVersions?.[displayPreviewLanguageVersion]?.courseTitle ||
    course.title;

  const isMandatoryShown = Boolean(course.isMandatoryInView);

  const [coursePublishState, dateToShow, messageToShow] = useMemo(() => {
    if (!course) return [null, null, null];
    return Date.parse(course?.startDate as string) > now
      ? ['opening', course.startDate, messages.courseOpens]
      : Date.parse(course?.endDate as string) >= now
      ? ['ending', course.endDate, messages.courseEnds]
      : Date.parse(course?.endDate as string) < now
      ? ['ended', course.endDate, messages.courseHasEnded]
      : [null, null, null];
  }, [course, messages, now]);

  return (
    <div className={`info ${isProfilePage ? 'info-profile' : ''}`}>
      <div
        className="picture"
        style={{
          backgroundImage: `url(${course.picture})`,
          backgroundPosition: course.picturePosition
        }}
      />

      <div className="title">
        {titleLink ? (
          <Link
            href={titleLink}
            linkColor="inherit"
            style={{ display: 'inline-block' }}
          >
            <div className="title-text">{courseTitle}</div>
          </Link>
        ) : (
          <div className="title-text">{courseTitle}</div>
        )}
      </div>

      {isMandatoryShown || coursePublishState ? (
        <div className="date-row">
          {isMandatoryShown ? (
            <span className="mandatory">{messages.commonMandatory}</span>
          ) : null}

          {isMandatoryShown && coursePublishState ? (
            <span className="divider">{'•'}</span>
          ) : null}

          {coursePublishState ? (
            <span
              className={`date ${
                coursePublishState === 'ended'
                  ? 'date-danger'
                  : coursePublishState === 'ending'
                  ? 'date-warning'
                  : ''
              }`}
            >
              <span className="date-label">{messageToShow}</span>
              <DisplayFormattedDate date={dateToShow} />
              {coursePublishState !== 'ended' ? (
                <span className="date-time">
                  <DisplayFormattedDate date={dateToShow} dateFormat="HH:mm" />
                </span>
              ) : null}
            </span>
          ) : null}
        </div>
      ) : null}

      {courseDescription ? (
        <div className="description">
          <RichTextValue html={courseDescription} />
        </div>
      ) : null}

      <div className="details">
        <DisplayAttributeInProductPage
          customAttributes={course.customAttributes}
          inProfile={isProfilePage}
          labelSpacing={labelSpacing}
          position="at start"
          productType="course"
        />

        {!serverConfig.productHideOwner ? (
          <ContentLabel
            horizontal={!isProfilePage}
            label={messages.commonOwners}
            spacing={labelSpacing}
          >
            <ContentValue>
              <div className="owners">
                {course.owners.map((owner) => (
                  <User
                    key={owner.id}
                    size="small"
                    showEmail={false}
                    showEmailTooltip={false}
                    titleStyle={{ fontSize: '1.4rem' }}
                    user={owner}
                  />
                ))}
              </div>
            </ContentValue>
          </ContentLabel>
        ) : null}

        {course.categories?.length ? (
          <ContentLabel
            horizontal={!isProfilePage}
            label={messages.commonCategories}
            spacing={labelSpacing}
          >
            <ContentValue>
              <div className="categories">
                {course.categories.map((category) => (
                  <Badge key={category} text={category} size="medium" />
                ))}
              </div>
            </ContentValue>
          </ContentLabel>
        ) : null}

        {course.completionWorkloadValue &&
        (serverConfig.courseCompletionRequireWorkload ||
          course.completionWorkload) ? (
          <ContentLabel
            horizontal={!isProfilePage}
            label={messages.settingsCompletionWorkload}
            spacing={labelSpacing}
          >
            <ContentValue>
              <div className="workload">
                {getMessageWithPlaceholders({
                  message: messages.commonNumberOfHours,
                  placeholders: {
                    numberOfHours: new Intl.NumberFormat(language).format(
                      course.completionWorkloadValue
                    )
                  }
                })}
              </div>
            </ContentValue>
          </ContentLabel>
        ) : null}

        {course.competencies?.length ? (
          <ContentLabel
            horizontal={!isProfilePage}
            label={messages.competenciesGranted}
            spacing={labelSpacing}
            style={{ whiteSpace: 'nowrap' }}
            tooltip={messages.coursesCompetenciesGrantedInfo}
          >
            <ContentValue>
              <FlexContainer style={{ flexWrap: 'wrap', gap: 10 }}>
                {course.competencies.map((competency) => (
                  <CompetenciesProductBadge
                    key={competency.id}
                    competency={competency}
                  />
                ))}
              </FlexContainer>
            </ContentValue>
          </ContentLabel>
        ) : null}

        {course.openingHours?.isEnabled ? (
          <ContentLabel
            horizontal={!isProfilePage}
            label={messages.courseOpeningHours}
            spacing={labelSpacing}
          >
            <div className="opening-hours">
              {getOpeningHoursWeekDays({ messages }).map((weekDay) => {
                const dayData =
                  course.openingHours?.weekDays?.[
                    weekDay.value as CourseWeekDay
                  ];

                return (
                  <div className="opening-hours-day" key={weekDay.value}>
                    <div className="opening-hours-label">{weekDay.label}</div>

                    <div className="opening-hours-value">
                      {dayData?.isOpen
                        ? `${dayData?.startTime} - ${dayData?.endTime}`
                        : messages.commonClosed}
                    </div>
                  </div>
                );
              })}
            </div>
          </ContentLabel>
        ) : null}

        <DisplayAttributeInProductPage
          customAttributes={course.customAttributes}
          inProfile={isProfilePage}
          labelSpacing={labelSpacing}
          productType="course"
        />

        {Object.keys(availableLanguages).length > 1 ? (
          <>
            <ContentLabel
              horizontal={!isProfilePage}
              label={messages.courseLanguageVersions}
              spacing={labelSpacing}
            >
              <ContentValue>
                <div className="available-languages">
                  {Object.entries(availableLanguages)
                    .sort((a, b) =>
                      a[1].capitalizedName.localeCompare(b[1].capitalizedName)
                    )
                    .map(([language, languageObject]) => (
                      <button
                        className={`available-language ${
                          language === displayPreviewLanguageVersion
                            ? 'available-language-selected'
                            : ''
                        } focus-box-shadow`}
                        key={language}
                        onClick={() => handleDisplayLanguageChange(language)}
                      >
                        <FlagIcon lang={language} style={{ marginTop: 1 }} />
                        <span className="available-language-text">
                          {languageObject.capitalizedName}
                        </span>
                      </button>
                    ))}
                </div>
              </ContentValue>
            </ContentLabel>

            {!isProfilePage ? (
              <ContentLabel
                horizontal={!isProfilePage}
                label={messages.courseLanguageVersionsSelected}
                spacing={labelSpacing}
              >
                <ContentValue>
                  <div
                    className={`select-language ${
                      selectedLanguage ? 'select-language-active' : ''
                    }`}
                  >
                    {selectedLanguage ? (
                      <div className="select-language-selected">
                        <FlagIcon lang={selectedLanguage} />
                        <span>
                          {availableLanguages[selectedLanguage].capitalizedName}
                        </span>
                      </div>
                    ) : (
                      <div className="select-language-unselected">
                        {messages.commonNotSelected}
                      </div>
                    )}
                    {enableCourseCompletionLanguageSelection && (
                      <CourseLanguageSelection
                        course={course}
                        selectedLanguage={selectedLanguage}
                        onSubmitSuccess={onLanguageVersionChange}
                      />
                    )}
                  </div>
                </ContentValue>
              </ContentLabel>
            ) : null}
          </>
        ) : null}
      </div>

      <style jsx>{`
        .picture {
          background-size: cover;
          border-radius: ${theme.cardBorderRadius};
          height: 140px;
          width: 100%;
        }
        .title {
          margin-top: 20px;
        }
        .title-text {
          color: ${theme.textColor};
          font-size: 1.8rem;
          font-weight: 700;
        }
        .date-row {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 600;
          margin-top: 5px;
        }
        .mandatory {
          color: ${theme.dangerColor};
        }
        .divider {
          margin: 0 7px;
        }
        .date-label {
          margin-right: 5px;
        }
        .date-time {
          margin-left: 5px;
        }
        .date-warning {
          color: ${theme.warningStandaloneColor};
        }
        .date-danger {
          color: ${theme.dangerColor};
        }
        .description {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          line-height: 1.5;
          margin-top: 15px;
        }
        .details {
          margin-top: 30px;
        }
        .owners {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        .categories {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        .workload {
          font-size: 1.5rem;
          font-weight: 600;
        }
        .opening-hours {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        .opening-hours-day {
          display: flex;
          gap: 10px;
          justify-content: space-between;
          max-width: 300px;
          min-width: 220px;
        }
        .opening-hours-label {
          color: ${theme.textColor};
          font-weight: 400;
        }
        .opening-hours-value {
          color: ${theme.textColor};
          font-weight: 400;
        }
        .available-languages {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          position: relative;
          top: 3px;
        }
        .available-language {
          align-items: center;
          background-color: transparent;
          border: 0;
          border-radius: ${theme.buttonBorderRadius};
          color: ${theme.textColor};
          cursor: pointer;
          display: flex;
          font-family: inherit;
          font-size: 1.5rem;
          outline: none;
          padding: 0;
          gap: 5px;
        }
        .available-language:focus {
          box-shadow: 0 0 0 5px rgba(0, 0, 0, 0.15);
        }
        .available-language:hover {
          text-decoration: underline;
        }
        .available-language-selected {
          font-weight: 600;
          text-decoration: underline;
        }
        .select-language {
          align-items: center;
          display: flex;
          gap: 10px;
          position: relative;
          top: 1px;
        }
        .select-language-active {
          top: 3px;
        }
        .select-language-selected {
          align-items: center;
          color: ${theme.textColor};
          display: flex;
          font-size: 1.5rem;
          gap: 5px;
        }
        .selected-language:hover {
          text-decoration: none;
        }
        .select-language-unselected {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
        }
        @media (min-width: ${breakpoints.small}px) {
          .picture {
            height: 170px;
          }
          .title {
            font-size: 2rem;
          }
        }
        @media (min-width: ${breakpoints.medium}px) {
          .picture {
            height: 220px;
          }
        }
        @media (min-width: ${breakpoints.large}px) {
          .info-profile .picture {
            height: 140px;
          }
          .info-profile .title {
            font-size: 1.8rem;
          }
        }
      `}</style>
    </div>
  );
};

export default CourseInfo;
